// Guild e cerco: criar ou entrar numa guild, membros e cargos, o boss da guild e o cerco semanal das fortalezas.
// Visual: estandartes medievais (vermelho, dourado, azul-real) — CSS em client/css-parts/guild.css (prefixo .gd-).
import { useEffect, useState } from 'preact/hooks';
import type { GuildBossView } from '../../../../shared/src/game/guildboss';
import { GUILD_MAX, RANK_NAMES, type GuildInfo, type GuildRank } from '../../../../shared/src/game/guild';

export interface GuildState { guild: GuildInfo | null; rank: string | null; invites: Array<{ id: number; name: string }>; siege: Array<{ id: string; name: string; min: number; max: number; bonus: number; owner: string | null; top: Array<{ name: string; points: number }> }>; weekEnds: number; createGold: number; boss?: GuildBossView | null }
type Send = (op: 'create' | 'invite' | 'accept' | 'decline' | 'leave' | 'kick' | 'promote' | 'demote' | 'motd', arg?: string, id?: number) => void;
const n = (v: number) => Math.round(v).toLocaleString('pt-BR');
const left = (ms: number) => { ms = Math.max(0, ms); const d = Math.floor(ms / 86400_000), h = Math.floor((ms % 86400_000) / 3600_000), m = Math.floor((ms % 3600_000) / 60_000); return d ? `${d}d ${h}h` : `${h}h ${String(m).padStart(2, '0')}m`; };
/** próxima meia-noite de Brasília (UTC-3): quando o boss da guild libera de novo */
const nextBrMidnight = (now: number) => { const BR = 3 * 3600_000; return Math.floor((now - BR) / 86400_000 + 1) * 86400_000 + BR; };

// cores do brasão: campo, carga e borda — escolhidas pelo id da guild (sempre as mesmas para a mesma guild)
const CRESTS = [
  { field: '#8e1b1b', field2: '#5a0f0f', charge: '#f0c75a' }, // vermelho e ouro
  { field: '#1f3f8f', field2: '#122659', charge: '#f0c75a' }, // azul-real e ouro
  { field: '#b8902c', field2: '#7a5c14', charge: '#7a1414' }, // ouro e vermelho
  { field: '#1f3f8f', field2: '#122659', charge: '#e9e4d6' }, // azul e prata
  { field: '#8e1b1b', field2: '#5a0f0f', charge: '#e9e4d6' }, // vermelho e prata
];
const CHARGES = ['⚔', '♜', '✠', '⚜', '☩'];
const initials = (s: string) => s.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');
const RANK_ORDER: Record<GuildRank, number> = { leader: 0, vice: 1, member: 2 };
const RANK_ICON: Record<GuildRank, string> = { leader: '👑', vice: '⚔', member: '🛡' };
const FORT_COLORS: Record<string, string> = { norte: '#1f3f8f', rubra: '#8e1b1b', sombria: '#4a3a6a' };

function Crest(props: { id: number; name: string; big?: boolean; empty?: boolean }) {
  const c = CRESTS[Math.abs(props.id) % CRESTS.length]!;
  return (
    <div class={'gd-crest' + (props.big ? ' big' : '') + (props.empty ? ' empty' : '')} style={{ '--f': c.field, '--f2': c.field2, '--c': c.charge } as any}>
      <span class="gd-crest-pole" />
      <span class="gd-crest-flag">
        <i class="gd-crest-charge">{props.empty ? '?' : CHARGES[Math.abs(props.id) % CHARGES.length]}</i>
        {!props.empty && <b class="gd-crest-ini">{initials(props.name)}</b>}
      </span>
    </div>
  );
}

export function Guild(props: { st: GuildState | null; me: number; bank: number; send: Send; level: number; inCity: boolean; onBoss: () => void }) {
  const [tab, setTab] = useState<'guild' | 'siege' | 'boss'>('guild');
  const [name, setName] = useState('');
  const [inv, setInv] = useState('');
  const [help, setHelp] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 30_000); return () => clearInterval(t); }, []);
  const st = props.st;
  if (!st) return <p class="muted">Carregando...</p>;
  const now = Date.now();
  const g = st.guild, boss = st.rank === 'leader' || st.rank === 'vice';
  const online = g ? g.members.filter((m) => m.online).length : 0;
  const forts = g ? st.siege.filter((f) => f.owner === g.name) : [];
  const b = st.boss ?? null;
  const bossWhy = !b ? '' : b.usedToday ? 'Você já enfrentou o boss hoje.' : props.level < b.minLevel ? `Pede level ${b.minLevel}.` : !props.inCity ? 'Entre pela cidade.' : '';
  const bossState: 'ok' | 'cd' | 'lock' = !b ? 'lock' : b.usedToday ? 'cd' : bossWhy ? 'lock' : 'ok';
  const members = g ? [...g.members].sort((a, c) => RANK_ORDER[a.rank] - RANK_ORDER[c.rank] || Number(c.online) - Number(a.online) || c.level - a.level) : [];
  const nameOk = name.trim().length >= 3, goldOk = props.bank >= st.createGold;

  return (
    <div class="gd">
      {/* cabeçalho heráldico: estandarte da guild e números */}
      <div class={'gd-head' + (g ? '' : ' none')}>
        {g ? <Crest id={g.id} name={g.name} big /> : <Crest id={0} name="" big empty />}
        <div class="gd-head-txt">
          <div class="gd-title">
            <span class="gd-title-name">{g ? g.name : 'Sem estandarte'}</span>
            <button class="gd-help" onClick={() => setHelp(!help)} title="Como funciona">?</button>
          </div>
          {g ? <>
            <div class="gd-sub">Seu cargo: <b class={'gd-rk ' + (st.rank ?? 'member')}>{RANK_NAMES[(st.rank ?? 'member') as GuildRank]}</b></div>
            <div class="gd-stats">
              <span title="Membros"><i>🛡</i><b>{g.members.length}</b>/{GUILD_MAX}</span>
              <span title="Membros online"><i class="gd-dot on" /><b>{online}</b> online</span>
              <span title="Fortalezas sob o estandarte da guild"><i>♜</i><b>{forts.length}</b> fortaleza{forts.length === 1 ? '' : 's'}</span>
              {b && <span title="Boss da guild"><i>🐉</i>Tier <b>{b.tier}</b></span>}
            </div>
          </> : <div class="gd-sub">Junte-se a uma guild para caçar pelo cerco e enfrentar o boss da guild.</div>}
        </div>
      </div>

      {(help || !g) && (
        <div class="gd-steps">
          <div><i>🏰</i><b>1. Funde ou entre</b><small>Crie a sua guild por {n(st.createGold)} gp do banco, ou aceite um convite de um líder.</small></div>
          <div><i>⚔</i><b>2. Cace pelo cerco</b><small>Cada abate numa hunt da faixa de uma fortaleza soma pontos para a guild durante a semana.</small></div>
          <div><i>👑</i><b>3. Tome o castelo</b><small>Domingo às 20h a guild com mais pontos leva a fortaleza: +3% de experiência naquela faixa.</small></div>
          <div><i>🐉</i><b>4. Boss da guild</b><small>1 luta por dia, 5 minutos, vida compartilhada. Quando cai, todos que bateram ganham prêmios.</small></div>
        </div>
      )}

      {/* abas em forma de flâmula */}
      <div class="gd-tabs">
        <button class={'gd-tab' + (tab === 'guild' ? ' on' : '')} onClick={() => setTab('guild')}>🛡 Guild</button>
        <button class={'gd-tab' + (tab === 'siege' ? ' on' : '')} onClick={() => setTab('siege')}>♜ Cerco de castelo</button>
        {b && <button class={'gd-tab' + (tab === 'boss' ? ' on' : '')} onClick={() => setTab('boss')}>🐉 Boss da guild{bossState === 'ok' && <i class="gd-ping" title="Disponível" />}</button>}
      </div>

      {tab === 'guild' && (g ? <>
        {(g.motd || boss) && (
          <div class="gd-motd">
            <small class="gd-kicker">Mensagem da guild</small>
            {g.motd ? <p>{g.motd}</p> : <p class="muted">Nenhuma mensagem. Escreva uma para os membros.</p>}
            {boss && <input key="motd" class="mk-search" placeholder="Nova mensagem da guild (Enter para salvar)" maxLength={200} defaultValue={g.motd} onChange={(e) => props.send('motd', (e.target as HTMLInputElement).value)} />}
          </div>
        )}
        <div class="gd-sec">Membros <small class="muted">· {online} de {g.members.length} online</small></div>
        <div class="gd-list">{members.map((m) => (
          <div key={m.id} class={'gd-mem ' + m.rank + (m.online ? ' on' : '') + (m.id === props.me ? ' me' : '')}>
            <span class={'gd-badge ' + m.rank} title={RANK_NAMES[m.rank]}>{RANK_ICON[m.rank]}</span>
            <div class="gd-mem-txt">
              <b><i class={'gd-dot' + (m.online ? ' on' : '')} title={m.online ? 'Online' : 'Offline'} />{m.name}{m.id === props.me && <small class="gd-you">você</small>}</b>
              <small>{m.vocation} · level {m.level}</small>
            </div>
            <span class={'gd-rk ' + m.rank}>{RANK_NAMES[m.rank]}</span>
            <span class="gd-mem-act">{boss && m.id !== props.me && m.rank !== 'leader' && <>
              {st.rank === 'leader' && (m.rank === 'member' ? <button class="tb-button" onClick={() => props.send('promote', undefined, m.id)}>Vice</button> : <button class="tb-button" onClick={() => props.send('demote', undefined, m.id)}>Membro</button>)}
              <button class="tb-button" onClick={() => props.send('kick', undefined, m.id)}>Expulsar</button></>}</span>
          </div>
        ))}</div>
        <div class="gd-foot">
          {boss && <div class="gd-invite"><input key="inv" class="mk-search" placeholder="Convidar personagem" value={inv} onInput={(e) => setInv((e.target as HTMLInputElement).value)} />
            <button class="tb-button green" disabled={!inv.trim()} onClick={() => { props.send('invite', inv.trim()); setInv(''); }}>Convidar</button></div>}
          <button class="tb-button gd-leave" onClick={() => props.send('leave')}>Sair da guild</button>
        </div>
      </> : <>
        {st.invites.length > 0 && <>
          <div class="gd-sec">Convites recebidos <small class="muted">· {st.invites.length}</small></div>
          <div class="gd-invites">{st.invites.map((i) => (
            <div key={i.id} class="gd-inv">
              <Crest id={i.id} name={i.name} />
              <div class="gd-inv-txt"><small class="gd-kicker">Convite da guild</small><b>{i.name}</b></div>
              <span><button class="tb-button green" onClick={() => props.send('accept', undefined, i.id)}>Entrar</button> <button class="tb-button" onClick={() => props.send('decline', undefined, i.id)}>Recusar</button></span>
            </div>
          ))}</div>
        </>}
        <div class="gd-sec">Fundar uma guild</div>
        <div class="gd-found">
          <Crest id={name.length} name={name.trim() || '?'} />
          <div class="gd-found-txt">
            <input key="new" class="mk-search" placeholder="Nome da nova guild (3 a 24 letras)" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
            <small class={goldOk ? 'muted' : 'gd-warn'}>Custo: <b class="gd-gold">{n(st.createGold)} gp</b> do banco{goldOk ? '' : ` · você tem ${n(props.bank)} gp`}</small>
          </div>
          <button class="tb-button green" disabled={!goldOk || !nameOk} onClick={() => props.send('create', name.trim())}>Criar ({n(st.createGold)} gp)</button>
        </div>
      </>)}

      {tab === 'boss' && b && (
        <div class={'gd-boss ' + bossState}>
          <div class="gd-boss-top">
            <span class="gd-boss-emb">🐉</span>
            <div class="gd-boss-txt">
              <small class="gd-kicker">Tier {b.tier} · derrotado {b.kills} vez(es)</small>
              <b class="gd-boss-name">{b.name}</b>
            </div>
            <span class={'gd-state ' + bossState}>{bossState === 'ok' ? 'Disponível' : bossState === 'cd' ? `Volta em ${left(nextBrMidnight(now) - now)}` : 'Bloqueado'}</span>
          </div>
          <div class="gd-hp" title="Vida compartilhada do boss"><i style={{ width: `${Math.max(0, Math.min(100, (b.hp / b.max) * 100))}%` }} /><span>{n(b.hp)} / {n(b.max)}</span></div>
          <p class="gd-note">Cada membro enfrenta o boss 1 vez por dia, sozinho, por 5 minutos. Todo o dano sai da vida compartilhada.
            Quando ele cai, todos que causaram dano ganham gold, fragmentos de pedra de proteção e uma gema, e o boss volta mais forte.</p>
          <div class="gd-sec">Maior dano neste tier</div>
          <Ranking rows={b.top.map((t) => ({ name: t.name, v: t.dmg }))} empty="Ninguém causou dano neste tier ainda." />
          <div class="gd-cta">
            <button class="tb-button green" disabled={!!bossWhy} onClick={props.onBoss}>Enfrentar o boss</button>
            <small class="muted">{bossWhy || 'Você entra sozinho por 5 minutos.'}</small>
          </div>
        </div>
      )}

      {tab === 'siege' && <>
        <div class="gd-siege-head">
          <span class="gd-state live">Em andamento</span>
          <span>O cerco termina em <b class="gd-gold">{left(st.weekEnds - now)}</b> · domingo às 20h</span>
        </div>
        <p class="gd-note">Cerco assíncrono: durante a semana, cada abate de um membro numa hunt da faixa de level da fortaleza vale pontos para a guild.
          No domingo às 20h a guild com mais pontos toma a fortaleza, e os membros ganham o bônus de experiência ao caçar naquela faixa até o próximo cerco.</p>
        <div class="gd-forts">
          {st.siege.map((f) => {
            const mine = !!g && f.owner === g.name;
            const pos = g ? f.top.findIndex((t) => t.name === g.name) : -1;
            const myLevel = props.level >= f.min && props.level <= f.max;
            return (
              <div key={f.id} class={'gd-fort' + (mine ? ' mine' : '') + (myLevel ? ' here' : '')} style={{ '--fc': FORT_COLORS[f.id] ?? '#5a4a2a' } as any}>
                <div class="gd-fort-top">
                  <span class="gd-tower">♜</span>
                  <div class="gd-fort-txt">
                    <b>{f.name}</b>
                    <small>Hunts do level {f.min} {f.max > 9999 ? 'em diante' : `ao ${f.max}`} · <b class="gd-gold">+{f.bonus}% XP</b></small>
                  </div>
                  {myLevel && <span class="gd-tag" title="A faixa do seu level">sua faixa</span>}
                </div>
                <div class="gd-owner">
                  <small>Dona atual</small>
                  <b class={mine ? 'gd-gold' : f.owner ? '' : 'muted'}>{mine && '👑 '}{f.owner ?? 'Ninguém'}</b>
                </div>
                <Ranking rows={f.top.map((t) => ({ name: t.name, v: t.points }))} hl={g?.name} empty="Sem pontos nesta semana." />
                {g && <small class="gd-mypos">{pos >= 0 ? (pos === 0 ? 'Sua guild lidera! Mantenha a caça.' : `Sua guild está em ${pos + 1}º lugar.`) : myLevel ? 'Sua guild ainda não pontuou aqui. Cace nesta faixa!' : 'Sua guild ainda não pontuou aqui.'}</small>}
              </div>
            );
          })}
        </div>
      </>}
    </div>
  );
}

function Ranking(props: { rows: Array<{ name: string; v: number }>; hl?: string; empty: string }) {
  if (!props.rows.length) return <div class="gd-rank-empty muted">{props.empty}</div>;
  const top = Math.max(1, ...props.rows.map((r) => r.v));
  return (
    <div class="gd-rank">{props.rows.map((r, i) => (
      <div key={r.name} class={'gd-rank-row' + (r.name === props.hl ? ' hl' : '')}>
        <span class={'gd-pos p' + (i + 1)}>{i + 1}º</span>
        <span class="gd-rank-bar"><i style={{ width: (r.v / top) * 100 + '%' }} /><b>{r.name}</b></span>
        <span class="gd-rank-v">{n(r.v)}</span>
      </div>
    ))}</div>
  );
}
